import { ChevronDownIcon } from "@radix-ui/react-icons";
import { Check, Loader2 } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useWebsitePageSearch } from "@/hooks/api/use-website-page-search";
import { useWebsitePagesByUrls } from "@/hooks/api/use-website-pages-by-urls";
import { useWebpageSelection } from "@/hooks/use-webpage-selection";
import { cn } from "@/lib/utils";

interface WebpageSelectorProps {
  channelId?: string;
  startDate?: string;
  endDate?: string;
  className?: string;
}

/**
 * Webpage selector component
 *
 * Allows searching and selecting website pages with a dropdown.
 * Shows recent pages and search results.
 */
export function WebpageSelector({
  channelId,
  startDate,
  endDate,
  className,
}: WebpageSelectorProps) {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const { selectedWebpageUrl, initialWebpage, handleWebpageChange } =
    useWebpageSelection();

  // Fetch all webpages based on filters (no search query to get all results)
  const { data: webpagesData, isFetching: isLoadingWebpages } =
    useWebsitePageSearch({
      search: searchQuery,
      channelId,
      startDate,
      endDate,
    });

  // Fetch selected webpage details if not found in recent webpages
  const { data: webpageData } = useWebsitePagesByUrls(
    !initialWebpage && selectedWebpageUrl ? [selectedWebpageUrl] : [],
  );

  // Get all available webpages
  const allWebpages = webpagesData?.results || [];

  // Filter webpages based on search query
  const filteredWebpages = searchQuery
    ? allWebpages.filter(
        (webpage) =>
          webpage.url.toLowerCase().includes(searchQuery.toLowerCase()) ||
          webpage.page_type.toLowerCase().includes(searchQuery.toLowerCase()),
      )
    : allWebpages;

  // Determine which webpage to show as selected
  const selectedWebpage = useMemo(() => {
    return (
      initialWebpage ??
      (webpageData?.[0]
        ? {
            url: webpageData[0].url,
            page_type: webpageData[0].page_type,
            total_engagements: webpageData[0].total_engagements,
            people_engaged: webpageData[0].people_engaged,
            sessions: webpageData[0].sessions,
            accounts_engaged: 0,
            opportunities_influenced: 0,
          }
        : undefined)
    );
  }, [initialWebpage, webpageData]);

  // Add the selected webpage to recent webpages when coming from a URL
  useEffect(() => {
    if (!initialWebpage && selectedWebpageUrl && selectedWebpage) {
      handleWebpageChange(selectedWebpage.url, selectedWebpage);
    }
  }, [
    initialWebpage,
    selectedWebpageUrl,
    selectedWebpage,
    handleWebpageChange,
  ]);

  // Handle webpage selection
  const handleWebpageSelect = (url: string) => {
    const webpage = filteredWebpages.find((w) => w.url === url);
    if (webpage) {
      handleWebpageChange(url, webpage);
    }
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          data-testid="webpage-selector-trigger"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn(
            "min-w-[300px] max-w-full justify-between gap-6 bg-white dark:bg-[hsl(195_30%_12%)]",
            className,
          )}
          disabled={isLoadingWebpages}
        >
          {isLoadingWebpages ? (
            <div className="flex items-center space-x-2">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span>Loading pages...</span>
            </div>
          ) : selectedWebpage ? (
            <div className="flex items-center gap-2 min-w-0 flex-1">
              <span
                className="truncate font-medium"
                title={selectedWebpage.url}
              >
                {selectedWebpage.url}
              </span>
              <Badge className="shrink-0">{selectedWebpage.page_type}</Badge>
            </div>
          ) : (
            "Select page..."
          )}
          <ChevronDownIcon className="h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[640px] max-w-[90vw] p-0" align="start">
        <Command>
          <CommandInput
            data-testid="webpage-selector-input"
            placeholder="Search by URL, domain, or path..."
            value={searchQuery}
            onValueChange={setSearchQuery}
          />
          <CommandList>
            <CommandEmpty>
              {isLoadingWebpages ? (
                <div className="flex items-center justify-center space-x-2 py-4">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span>Loading pages...</span>
                </div>
              ) : (
                "No pages found."
              )}
            </CommandEmpty>
            <CommandGroup>
              {filteredWebpages.map((webpage) => (
                <CommandItem
                  key={webpage.url}
                  value={webpage.url}
                  onSelect={() => handleWebpageSelect(webpage.url)}
                  className="flex items-center justify-between group"
                >
                  <div className="flex items-center space-x-2 flex-1 min-w-0">
                    <Check
                      className={cn(
                        "h-4 w-4 flex-shrink-0",
                        selectedWebpageUrl === webpage.url
                          ? "opacity-100"
                          : "opacity-0",
                      )}
                    />
                    <div className="flex flex-row justify-between flex-1 min-w-0">
                      <span
                        className="font-medium truncate"
                        title={webpage.url}
                      >
                        {webpage.url}
                      </span>
                      <Badge>{webpage.page_type}</Badge>
                    </div>
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
